import React, { Fragment, useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import LoadingBackdrop from '../../../LoadingBackdrop/LoadingBackdrop';
import { useTheme } from '@mui/material/styles';
import ChatGPTPresetDrawer from '../ChatGPTPresetDrawer';
import ContentBox from '../ContentBox/ContentBox';
import FollowUpButton from '../FollowUpButton/FollowUpButton';
import { withRouter } from 'react-router';
import { Grid, TextField, Typography, Radio, RadioGroup, FormControlLabel } from '@mui/material';
import { connect } from 'react-redux';
import { toggleControl } from '../../../../store/control/duck';
import { openaiGenerateWithTextStreaming } from '../../../../store/openai/api';
import { decreaseGPTQuota, handleError, resetGPTQuota, validateGPTQuota } from '../../../../utils/openai';
import { useForceUpdate } from '../../../../utils/hooks';
import { toast } from 'react-toastify';
import IconToast from '../../../NewToast';
import { trackDirect } from '../../../../store/analytics/api';
import { tracking } from '../../../../utils/tracking';
import { hasCampaignCode } from '../../../../utils/user';
import ExampleChips from '../ExampleChips/ExampleChips';
import { multilineTextInputSx, textInputSx } from '../Inputs/inputStyles';
import FormLabelWithIcon from '../Inputs/FormLabelWithIcon';
import { LanguagePicker } from '../Inputs/Inputs';
import SuggestionTypeSwitchButton from '../ChatGPTIceBreakingDrawerV2/SuggestionTypeSwitchButton';
import ActionButton from '../ActionButton/ActionButton';

// The 關鍵字 box takes a topic of at most 20 characters, so the chip label IS
// the value. The 添加 Emojis box takes a whole message, so its chips are
// categories that fill a sentence the adviser then edits.
const IG_POST_EXAMPLES = [
  { labelId: 'gpt-example-ig-post-1' },
  { labelId: 'gpt-example-ig-post-2' },
  { labelId: 'gpt-example-ig-post-3' }
];

const FUN_EXAMPLES = [
  { labelId: 'gpt-example-fun-1', valueId: 'gpt-example-fun-1-text' },
  { labelId: 'gpt-example-fun-2', valueId: 'gpt-example-fun-2-text' },
  { labelId: 'gpt-example-fun-3', valueId: 'gpt-example-fun-3-text' }
];

const tabOptions = [
  { value: 'ig-post', label: 'chat-gpt-options-ig-post' },
  { value: 'fun', label: 'chat-gpt-options-fun' }
];

const scriptLanguage = {
  'Traditional Chinese': 'Natual spoken Cantonese',
  English: 'Natural spoken English',
  'Simplified Chinese': 'Natural spoken Mandarin'
};

export const parseVariants = text => {
  const rawText = typeof text === 'string' ? text : '';
  const variants = {};
  const variantPattern = /\[\[VARIANT_(1|2)\]\]([\s\S]*?)\[\[\/VARIANT_\1\]\]/g;
  let match;

  while ((match = variantPattern.exec(rawText))) {
    variants[match[1]] = match[2].trim();
  }

  if (variants[1] && variants[2]) {
    return [variants[1], variants[2]];
  }

  const separatedVariants = rawText
    .split('-----')
    .map(variant => variant.trim())
    .filter(Boolean);

  return separatedVariants.length > 1 ? separatedVariants : rawText ? [rawText] : [];
};

const streamOnce = (promptId, data, setOutput) =>
  new Promise((resolve, reject) => {
    openaiGenerateWithTextStreaming(promptId, data, [], setOutput, resolve, null).catch(reject);
  });

export const ResultCard = ({
  title,
  text,
  variants = [],
  activeVariantIndex = 0,
  variant,
  onVariantChange,
  versionLabels = []
}) => {
  const hasVariants = variants.length === 2;
  const visibleText = hasVariants ? variants[activeVariantIndex] || variants[0] : text;

  return (
    <Grid>
      <Typography>{title}</Typography>
      {hasVariants && (
        <RadioGroup
          row
          aria-label={`${title} version`}
          name={`${variant}-version`}
          value={String(activeVariantIndex)}
          onChange={event => onVariantChange(Number(event.target.value))}
        >
          {[0, 1].map(index => (
            <FormControlLabel
              key={index}
              value={String(index)}
              control={<Radio color="primary" style={{ paddingTop: '2px', paddingBottom: '2px' }} />}
              label={versionLabels[index] || `Version ${index + 1}`}
            />
          ))}
        </RadioGroup>
      )}
      <ContentBox text={visibleText} />
    </Grid>
  );
};

const ChatGPTIGPostDrawer = props => {
  const { intl, history, userDetails, chatGPTIGPostDrawer, toggleControl, teamCampaignCodes } = props;

  const [tab, setTab] = useState('ig-post');

  // IG Post states
  const [userText, setUserText] = useState('');
  const [prompt, setPrompt] = useState('generate_social_media_by_keyword_all');
  const [userTextError, setUserTextError] = useState(undefined);
  const [text, setText] = useState('');
  const [scriptText, setScriptText] = useState('');
  const [postVariantIndex, setPostVariantIndex] = useState(0);
  const [scriptVariantIndex, setScriptVariantIndex] = useState(0);

  // Fun (Add Emojis) states
  const [funUserText, setFunUserText] = useState('');
  const [funUserTextError, setFunUserTextError] = useState(undefined);
  const [funText, setFunText] = useState('');

  const [loading, setLoading] = useState(false);
  const forceUpdate = useForceUpdate();
  const theme = useTheme();
  const [language, setLanguage] = useState(
    history.location.state?.language ? history.location.state.language : 'Traditional Chinese'
  );
  const shouldShowRecruit = hasCampaignCode(userDetails, teamCampaignCodes, 'FEATURE_RECRUIT');
  const { client, closed, preset } = chatGPTIGPostDrawer || {};
  const isOpen = !!chatGPTIGPostDrawer && !closed;

  /**
   * The 場景 radio, set from the door the adviser came in by.
   *
   * `prompt` is the one piece of state `onExited` never cleared, so it used to
   * persist for the life of the session. That was invisible while every door
   * was the same door; now that 招募 mode opens this drawer with a preset, a
   * sticky value would mean an adviser who used recruitment copy once got it
   * again on their next sales post, silently. So this settles the radio on
   * every open — to 招募 when that is what was asked for and the adviser has
   * the campaign code, and back to 全部 otherwise.
   *
   * Keyed on `isOpen` alone: changing the radio by hand must not re-run it.
   */
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const wantsRecruit = preset === 'recruit' && shouldShowRecruit;
    setPrompt(wantsRecruit ? 'generate_social_media_by_keyword_recruit' : 'generate_social_media_by_keyword_all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  const postVariants = parseVariants(text);
  const scriptVariants = parseVariants(scriptText);
  const activePostText = postVariants[postVariantIndex] || postVariants[0] || '';
  const options = activePostText
    .split('-----')
    .filter(option => option.trim() !== '')
    .map(option => option.trim());

  const onClose = () => {
    setScriptText('');
    setPostVariantIndex(0);
    setScriptVariantIndex(0);
    toggleControl('chatGPTIGPostDrawer', {
      ...chatGPTIGPostDrawer,
      closed: true
    });
  };

  const onExited = () => {
    toggleControl('chatGPTIGPostDrawer', false);
    // The tab, like everything below it, belongs to the visit and not to the
    // adviser. It was the one piece left behind, and the drawer's title is
    // fixed to 短片腳本生成器 while only the subtitle and the body follow the
    // tab — so an adviser who used 添加 Emojis once tapped a card named
    // 短片腳本生成器 ever after and got the emoji tool, under a heading still
    // naming the one they asked for.
    setTab('ig-post');
    setUserText('');
    setUserTextError(undefined);
    setText('');
    setScriptText('');
    setPostVariantIndex(0);
    setScriptVariantIndex(0);
    setFunUserText('');
    setFunUserTextError(undefined);
    setFunText('');
  };

  const onFeedback = () => {
    toggleControl('chatGPTFeedbackDialog', {
      client: client,
      extraContent: tab === 'ig-post' ? [text, scriptText].filter(Boolean).join('\n\n') : funText,
      onSubmit: () => {
        resetGPTQuota();
        forceUpdate();
      }
    });
  };

  const onChangeInputMessage = event => {
    const targetValue = event.target.value;
    if (targetValue.length <= 20) {
      setUserText(event.target.value);
      setUserTextError(undefined);
    }
  };

  const onChangeFunInputMessage = event => {
    const targetValue = event.target.value;
    if (targetValue.length <= 200) {
      setFunUserText(event.target.value);
      setFunUserTextError(undefined);
    }
  };

  const send = async () => {
    if ((userText || '').length > 20) {
      setUserTextError(intl.formatMessage({ id: 'chat-gpt-ig-post-drawer-max-20-char' }));
      return;
    }

    try {
      validateGPTQuota();
      setText('');
      setScriptText('');
      setPostVariantIndex(0);
      setScriptVariantIndex(0);
      setLoading(true);
      trackDirect('useAiFeature');
      trackDirect('generateSocialMediaPost');
      tracking('AI: Generate Social Media Post');

      const generatedScriptText = await streamOnce(
        'generate_social_media_by_keyword_script',
        { keyword: userText, language: scriptLanguage[language] },
        setScriptText
      );
      decreaseGPTQuota();

      if ((generatedScriptText || '').trim()) {
        await streamOnce(prompt, { keyword: userText, language, script: generatedScriptText }, setText);
        decreaseGPTQuota();
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const sendFun = async () => {
    if ((funUserText || '').length > 200) {
      setFunUserTextError(intl.formatMessage({ id: 'chat-gpt-fun-drawer-max-200-char' }));
      return;
    }

    try {
      validateGPTQuota();
      setLoading(true);
      trackDirect('useAiFeature');
      trackDirect('addEmojiToMessage');
      tracking('AI: Add Emoji To Message');

      await openaiGenerateWithTextStreaming(
        'rewrite_message_with_emoji',
        { text: funUserText },
        [],
        setFunText,
        null,
        setLoading
      );

      decreaseGPTQuota();
    } catch (error) {
      handleError(error);
      setLoading(false);
    }
  };

  const clear = () => {
    if (tab === 'ig-post') {
      setText('');
      setScriptText('');
      setPostVariantIndex(0);
      setScriptVariantIndex(0);
    } else {
      setFunText('');
    }
  };

  const onValue = value => {
    try {
      navigator.clipboard.writeText(value);

      toast.info(IconToast('success', intl.formatMessage({ id: 'success' })), {
        autoClose: false,
        className: 'new-toast'
      });
    } catch (error) {
      toast.info(IconToast('error', intl.formatMessage({ id: 'error' })), {
        autoClose: false,
        className: 'new-toast'
      });
    }
  };

  return (
    <Fragment>
      <LoadingBackdrop open={loading} />
      <ChatGPTPresetDrawer
        open={isOpen}
        onClose={onClose}
        onExited={onExited}
        onFeedback={onFeedback}
        title={intl.formatMessage({ id: 'chat-gpt-options-ig-post' })}
        // Follows the tab, because the two tabs are two different AIs sharing a
        // drawer: one turns a keyword into a post, the other decorates a message
        // you already have. A subtitle fixed to the 短片腳本 half would be wrong
        // half the time, and it is the half that says what to type.
        subtitle={intl.formatMessage({ id: tab === 'ig-post' ? 'gpt-subtitle-ig-post' : 'gpt-subtitle-fun' })}
        showQuota
      >
        <Grid container spacing={2} wrap="nowrap" style={{ minHeight: '100%' }} sx={{ flexDirection: 'column' }}>
          {/* The switch comes FIRST, above the description it changes. The two
              tabs are two different AIs, and the paragraph below says what to
              type for whichever one is selected — so reading top to bottom used
              to mean reading an instruction before knowing which tool it was
              for, then finding the selector underneath and having to re-read. */}
          <Grid
            style={{
              marginLeft: 'auto',
              marginRight: 'auto',
              width: 'fit-content'
            }}
          >
            <SuggestionTypeSwitchButton options={tabOptions} current={tab} onAction={next => setTab(next)} />
          </Grid>

          <Grid>
            <Typography style={{ whiteSpace: 'pre-wrap' }}>
              {tab === 'ig-post'
                ? intl.formatMessage({ id: 'chat-gpt-ig-post-drawer-content' })
                : intl.formatMessage({ id: 'chat-gpt-fun-drawer-content' })}
            </Typography>
          </Grid>

          {tab === 'ig-post' ? (
            <Fragment>
              <Grid>
                <Typography
                  sx={{
                    fontSize: 17,
                    fontWeight: 700,
                    color: '#1C1C1E',
                    mb: 1.5
                  }}
                >
                  {intl.formatMessage({ id: 'chat-gpt-ig-post-keyword' })}
                </Typography>
                <ExampleChips
                  labelId="gpt-examples-try-topics"
                  examples={IG_POST_EXAMPLES}
                  value={userText}
                  onSelect={value => {
                    setUserText(value);
                    setUserTextError(undefined);
                  }}
                />
                <TextField
                  variant="outlined"
                  fullWidth
                  value={userText}
                  onChange={onChangeInputMessage}
                  placeholder={intl.formatMessage({
                    id: 'chat-gpt-ig-post-drawer-input-placeholder'
                  })}
                  error={!!userTextError}
                  helperText={userTextError}
                  slotProps={{ input: { sx: textInputSx } }}
                />
              </Grid>
              <Grid>
                <FormLabelWithIcon iconName="Compass" labelText={intl.formatMessage({ id: 'theme' })} type="blue" />
                <RadioGroup
                  row
                  aria-label="theme"
                  name="theme"
                  value={prompt}
                  onChange={event => setPrompt(event.target.value)}
                >
                  <FormControlLabel
                    value="generate_social_media_by_keyword_all"
                    control={<Radio color="primary" style={{ paddingTop: '2px', paddingBottom: '2px' }} />}
                    label={intl.formatMessage({ id: 'All' })}
                  />
                  <FormControlLabel
                    value="generate_social_media_by_keyword_business"
                    control={<Radio color="primary" style={{ paddingTop: '2px', paddingBottom: '2px' }} />}
                    label={intl.formatMessage({ id: 'business' })}
                  />
                  <FormControlLabel
                    value="generate_social_media_by_keyword_non_business"
                    control={<Radio color="primary" style={{ paddingTop: '2px', paddingBottom: '2px' }} />}
                    label={intl.formatMessage({ id: 'non-business' })}
                  />
                  {shouldShowRecruit && (
                    <FormControlLabel
                      value="generate_social_media_by_keyword_recruit"
                      control={<Radio color="primary" style={{ paddingTop: '2px', paddingBottom: '2px' }} />}
                      label={'💼 ' + intl.formatMessage({ id: 'recruit' })}
                    />
                  )}
                </RadioGroup>
              </Grid>
              <Grid>
                {/* Was three hand-written FormControlLabels identical to the
                    ones LanguagePicker renders — same values, same labelIds,
                    same 2px Radio padding. Adding a language would have had to
                    be done here, in 個性化訊息, and in Inputs/constant.js, and
                    the three would have drifted the first time anyone forgot.

                    `radioGroupProps={{}}` is load-bearing: the picker's DEFAULT
                    centres the row, which this drawer never did. Passing an
                    empty object keeps the row left-aligned exactly as before. */}
                <LanguagePicker
                  intl={intl}
                  options={{ language }}
                  onValueChange={(field, value) => setLanguage(value)}
                  radioGroupProps={{}}
                />
              </Grid>
              <Grid>
                <ActionButton onClick={send} disabled={!userText || loading}>
                  {intl.formatMessage({ id: 'gpt-message-generate-cta' })}
                </ActionButton>
              </Grid>
              {/* Each card arrives with its own answer, which is also the order
                  `send` produces them in: the script first, then the post built
                  from it. They used to render whether or not anything had been
                  generated, so opening this drawer put two placeholder boxes
                  below the CTA — and, since this is the only drawer with two
                  output boxes, the same waiting hint twice — before the adviser
                  had pressed anything. The single-box drawers keep their empty
                  box: there it is the one thing saying where the answer lands,
                  and there is nothing for it to repeat. */}
              {scriptText && (
                <ResultCard
                  title={intl.formatMessage({
                    id: 'chat-gpt-ig-post-script-content'
                  })}
                  text={scriptText}
                  variants={scriptVariants}
                  activeVariantIndex={scriptVariantIndex}
                  onVariantChange={setScriptVariantIndex}
                  versionLabels={[
                    intl.formatMessage({ id: 'chat-gpt-ig-post-version-1' }),
                    intl.formatMessage({ id: 'chat-gpt-ig-post-version-2' })
                  ]}
                  variant="script"
                />
              )}
              {text && (
                <ResultCard
                  title={intl.formatMessage({
                    id: 'chat-gpt-ig-post-post-content'
                  })}
                  text={text}
                  variants={postVariants}
                  activeVariantIndex={postVariantIndex}
                  onVariantChange={setPostVariantIndex}
                  versionLabels={[
                    intl.formatMessage({ id: 'chat-gpt-ig-post-version-1' }),
                    intl.formatMessage({ id: 'chat-gpt-ig-post-version-2' })
                  ]}
                  variant="post"
                />
              )}
              <Grid>
                <Grid container wrap="nowrap" sx={{ flexDirection: 'column', rowGap: '6px' }}>
                  {/* {options.length >= 1 && (
                    <Grid>
                      {<span style={{ fontSize: 16 }}>{intl.formatMessage({ id: 'click-option-to-copy' })}</span>}
                      {options.map((option, index) => (
                        <FollowUpButton
                          key={index}
                          onClick={() => onValue(option)}
                          color="primary"
                          style={{
                            color: theme.palette.primary.main,
                            marginLeft: 10
                          }}
                        >
                          {intl.formatMessage({ id: 'option-number' }, { number: index + 1 })}
                        </FollowUpButton>
                      ))}
                    </Grid>
                  )} */}
                  <Grid>
                    <Grid container sx={{ flexDirection: 'column', rowGap: '6px' }}>
                      {text && (
                        <Grid>
                          <Grid container spacing={1}>
                            <Grid>
                              <FollowUpButton onClick={clear}>
                                {intl.formatMessage({
                                  id: 'chat-gpt-follow-up-clear'
                                })}
                              </FollowUpButton>
                            </Grid>
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Fragment>
          ) : (
            <Fragment>
              <Grid>
                <ExampleChips
                  labelId="gpt-examples-try-messages"
                  examples={FUN_EXAMPLES}
                  value={funUserText}
                  onSelect={value => {
                    setFunUserText(value);
                    setFunUserTextError(undefined);
                  }}
                />
                <TextField
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  value={funUserText}
                  onChange={onChangeFunInputMessage}
                  placeholder={intl.formatMessage({
                    id: 'chat-gpt-fun-drawer-input-placeholder'
                  })}
                  error={!!funUserTextError}
                  helperText={funUserTextError}
                  slotProps={{ input: { sx: multilineTextInputSx } }}
                />
              </Grid>
              <Grid>
                <ActionButton onClick={sendFun} disabled={!funUserText || loading}>
                  {intl.formatMessage({ id: 'gpt-message-generate-cta' })}
                </ActionButton>
              </Grid>
              <Grid>
                <Grid container wrap="nowrap" sx={{ flexDirection: 'column', rowGap: '6px' }}>
                  <Grid>
                    <ContentBox text={funText} />
                  </Grid>
                  <Grid>
                    <Grid container sx={{ flexDirection: 'column', rowGap: '6px' }}>
                      {funText && (
                        <Grid>
                          <Grid container spacing={1}>
                            <Grid>
                              <FollowUpButton onClick={clear}>
                                {intl.formatMessage({
                                  id: 'chat-gpt-follow-up-clear'
                                })}
                              </FollowUpButton>
                            </Grid>
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Fragment>
          )}
        </Grid>
      </ChatGPTPresetDrawer>
    </Fragment>
  );
};

const container = connect(
  state => ({
    userDetails: state.user.userDetails,
    chatGPTIGPostDrawer: state.control.chatGPTIGPostDrawer,
    teamCampaignCodes: state.functions.campaignCodes
  }),
  {
    toggleControl
  }
)(ChatGPTIGPostDrawer);

export default injectIntl(withRouter(container));
